// The list pages' query strings. URLs are lower-case: the parameter names, and the values the pages hold in the
// legacy spelling — a status or type ("PendingApproval" ↔ pending_approval), a processor ("Central Payments" ↔ opencp).
// Text people typed (a search) and data from the API (an id, an operation name) go as they are.
import { providerKey, providerName } from '@qor/web-shared';
import { type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const toSnake = (v: string) => v.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
const toPascal = (v: string) => v.replace(/(^|_)([a-z])/g, (_: string, __: string, c: string) => c.toUpperCase());

/** How a parameter's value is written into the URL (out) and read back as the page holds it (in). */
const CODECS: Record<string, { out: (v: string) => string; in: (v: string) => string }> = {
  status: { out: toSnake, in: toPascal },
  type: { out: toSnake, in: toPascal },
  provider: { out: (v) => providerKey(v) ?? v, in: (v) => providerName(v) },
};

/** A list page's GET form: its non-empty fields become the query string of the page (the legacy method="get"). */
export function useFilterForm(path: string) {
  const navigate = useNavigate();
  return (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    navigate(`${path}${toQuery(new FormData(e.currentTarget))}`);
  };
}

export function toQuery(form: FormData | Record<string, string | number | boolean | null | undefined>): string {
  const params = new URLSearchParams();
  const entries = form instanceof FormData ? [...form.entries()] : Object.entries(form);
  for (const [k, v] of entries)
    if (v !== null && v !== undefined && v !== '' && v !== false)
      params.append(k, CODECS[k] ? CODECS[k].out(String(v)) : String(v));
  const s = params.toString();
  return s ? `?${s}` : '';
}

/** The page's query: string values (null when absent or empty) and the page number (1 when absent or bad). */
export function useQueryParams() {
  const [params] = useSearchParams();
  return {
    get: (name: string) => {
      const v = params.get(name);
      if (v === null || v.trim() === '') return null;
      return CODECS[name] ? CODECS[name].in(v) : v;
    },
    page: (name = 'page') => {
      const n = Number.parseInt(params.get(name) ?? '', 10);
      return Number.isFinite(n) && n > 0 ? n : 1;
    },
    flag: (name: string) => params.get(name)?.toLowerCase() === 'true',
  };
}
